import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "./types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { MapPin, X } from "lucide-react";
import { Card } from "@/components/ui/card";

interface AddressFieldProps {
  form: UseFormReturn<FormValues>;
  isEditing: boolean;
}

export const AddressField = ({ form, isEditing }: AddressFieldProps) => {
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const currentAddress = form.watch('address');

  useEffect(() => {
    if (currentAddress) {
      setSelectedAddress({ label: currentAddress, value: currentAddress });
    }
  }, [currentAddress]);

  const handleClearAddress = () => {
    setSelectedAddress(null);
    form.setValue('address', '', { shouldValidate: true });
  };

  if (!isEditing) {
    return (
      <Card className="transition-all duration-200 hover:shadow-md">
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Address</p>
              <p className="text-sm font-medium">{currentAddress || "Not set"}</p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="transition-all duration-200">
      <div className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Address</Label>
          </div>
          {!isManualAddress ? (
            <div className="space-y-2">
              <div className="relative">
                <GooglePlacesAutocomplete
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  selectProps={{
                    value: selectedAddress,
                    onChange: (val: any) => {
                      setSelectedAddress(val);
                      form.setValue('address', val.label, { shouldValidate: true });
                    },
                    placeholder: "Search for your address",
                    isClearable: true,
                    className: "w-full",
                    components: {
                      DropdownIndicator: () => <MapPin className="h-4 w-4 text-muted-foreground mr-2" />,
                      ClearIndicator: () => (
                        <button
                          onClick={handleClearAddress}
                          className="p-1 hover:bg-accent rounded-sm mr-1"
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ),
                      IndicatorSeparator: () => null,
                    },
                    styles: {
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: 'hsl(var(--background))',
                        borderColor: state.isFocused ? 'hsl(var(--ring))' : 'hsl(var(--input))',
                        boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--ring))' : 'none',
                        '&:hover': {
                          borderColor: 'hsl(var(--input))',
                        },
                        padding: '4px',
                        borderRadius: 'var(--radius)',
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        overflow: 'hidden',
                        borderRadius: 'var(--radius)',
                        marginTop: '4px',
                        zIndex: 50,
                      }),
                      menuList: (base) => ({
                        ...base,
                        padding: '4px',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? 'hsl(var(--accent))' : 'transparent',
                        color: state.isFocused ? 'hsl(var(--accent-foreground))' : 'hsl(var(--foreground))',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: 'var(--radius)',
                        '&:active': {
                          backgroundColor: 'hsl(var(--accent))',
                        },
                      }),
                      input: (base) => ({
                        ...base,
                        color: 'hsl(var(--foreground))',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: 'hsl(var(--foreground))',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: 'hsl(var(--muted-foreground))',
                      }),
                    },
                  }}
                />
              </div>
              <Button
                type="button"
                variant="link"
                onClick={() => setIsManualAddress(true)}
                className="px-0 h-auto font-normal text-primary"
              >
                Enter address manually instead
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input 
                        placeholder="Enter your complete address" 
                        className="bg-background border-input"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="link"
                onClick={() => setIsManualAddress(false)}
                className="px-0 h-auto font-normal text-primary"
              >
                Use address search instead
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}